import axios, { AxiosError, AxiosRequestConfig } from 'axios'

import { ExtendedAxiosRequestConfig } from '@/@types/axios'
import { ResponseSchema } from '@/@types/schemas/responses'
import { forceLogout } from '@/contexts/auth/force-logout'
import { getAccessToken } from '@/contexts/auth/token-storage'
import { env } from '@/env'
import {
  classifyAuthFailure,
  isAuthRoute,
} from '@/services/fintalk/api/auth-failure'
import { ensureFreshToken } from '@/services/fintalk/api/refresh-session'

const axiosInstance = axios.create({
  baseURL: env.VITE_FINTALK_BACKOFFICE_URL,
  timeout: 12000,
  headers: {
    'Content-Type': 'application/json',
    'x-fintalk-origin': 'studio/backoffice',
  },
})

// The proxy expects the bare token; every other host expects a bearer prefix.
const isBareTokenHost = (baseURL: string | undefined) =>
  baseURL === env.VITE_PROXY_URL

const applyAuthHeader = (config: ExtendedAxiosRequestConfig, token: string) => {
  const value = isBareTokenHost(config.baseURL) ? token : `Bearer ${token}`

  config.headers.set('Authorization', value)
}

const readTokenUsed = (config: ExtendedAxiosRequestConfig): string | null => {
  const header = config.headers?.get?.('Authorization')

  if (typeof header !== 'string' || !header) {
    return null
  }

  return header.startsWith('Bearer ') ? header.slice('Bearer '.length) : header
}

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken()

    if (!token) {
      return config
    }

    applyAuthHeader(config, token)

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

axiosInstance.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const initialRequest = error.config as
      | ExtendedAxiosRequestConfig
      | undefined

    if (!initialRequest || !error.response) {
      return Promise.reject(error)
    }

    if (isAuthRoute(initialRequest.url)) {
      return Promise.reject(error)
    }

    const failure = classifyAuthFailure(
      error.response.status,
      error.response.data,
    )

    if (failure === 'NONE') {
      return Promise.reject(error)
    }

    if (failure === 'FATAL') {
      forceLogout()
      return Promise.reject(error)
    }

    if (initialRequest._retry) {
      return Promise.reject(error)
    }

    const freshToken = await ensureFreshToken(readTokenUsed(initialRequest))

    if (!freshToken) {
      return Promise.reject(error)
    }

    initialRequest._retry = true

    applyAuthHeader(initialRequest, freshToken)

    return axiosInstance(initialRequest)
  },
)

export const api = async <DataResponseSchema>(
  path: string,
  config?: AxiosRequestConfig,
): Promise<ResponseSchema<DataResponseSchema>> => {
  try {
    const resp = await axiosInstance.request<DataResponseSchema>({
      url: path,
      ...config,
    })

    return {
      statusCode: resp.status,
      message: '',
      data: resp.data,
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return {
        statusCode: error.response.status,
        message: error.response.data?.message || 'Internal server error',
        data: error.response.data ?? null,
      }
    }

    return {
      statusCode: 500,
      message: error instanceof Error ? error.message : 'Unknown error',
      data: null,
    }
  }
}
